import type { ReactNode } from 'react';
import { TextInput, EmailInput, Text, Field, Select, Stack } from '@hotjar/hotsauce';
import type { FieldError } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import styled from '@emotion/styled';

import type { CountryCode } from '@hotjar/country-lib';
import { COUNTRIES } from '@hotjar/country-lib';
import { UNITED_STATES, CANADA, INDIA } from '@hotjar/webapp/constants/country-states';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { toKebabCase } from '@hotjar/webapp/lib/convert-string';

export type FormInputs = {
  companyName: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: CountryCode;
  state: string;
  county: string;
  zipCode: string;
  emailAddress: string;
  taxNumber: string;
};

const STATES = { US: UNITED_STATES, CA: CANADA, IN: INDIA } as const;

const S = {
  Field: styled(Field)`
    width: 100%;
  `,
  TextInput: styled(TextInput)`
    /* this is necessary for all fields to be the same width when they are next to each other */
    & input {
      width: 100%;
    }
  `,
};

interface FormFieldProps {
  t(key: string): string;
  register: ReturnType<typeof useForm>['register'];
  error: FieldError | undefined;
  fieldName: keyof FormInputs;
  isRequired?: boolean;
  type?: 'email' | 'text';
  children?: ReactNode;
}

function FormField({
  t,
  register,
  error,
  fieldName,
  isRequired = false,
  type,
  children,
}: FormFieldProps) {
  const translationKey = toKebabCase(fieldName);
  const id = fieldName;
  const props = type && {
    id,
    ref: register({ required: isRequired && t('error.required') }),
    name: fieldName,
    placeholder: t(`placeholder.${translationKey}`),
    isInvalid: !!error,
  };
  return (
    <S.Field
      labelText={t(`label.${translationKey}`)}
      helperText={t(`helper-text.${translationKey}`)}
      invalidText={error?.message}
      isInvalid={!!error}
      isRequired={isRequired}
      inputId={id}
    >
      {type === 'text' && <S.TextInput {...props} />}
      {type === 'email' && <EmailInput {...props} />}
      {children}
    </S.Field>
  );
}

interface Props {
  onSubmit(data: FormInputs): void;
  formId: string;
}

export function CompanyDetailsForm({ onSubmit, formId }: Props): React.ReactElement {
  const { t } = useTranslationWithPrefix('billing:company-details.form');
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormInputs>();

  const watchCountry = watch('country');
  const showState = Object.keys(STATES).includes(watchCountry);

  const sharedProps = { t, register };

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data))} id={formId}>
      <Stack preset="form">
        <Text as="p" textType="subtitle1">
          {t('subtitle.billing-address')}
        </Text>

        <FormField
          {...sharedProps}
          error={errors.addressLine1}
          fieldName="addressLine1"
          type="text"
          isRequired
        />

        <FormField
          {...sharedProps}
          error={errors.addressLine2}
          fieldName="addressLine2"
          type="text"
        />

        <Stack preset="default">
          <FormField {...sharedProps} error={errors.country} fieldName="country" isRequired>
            <Select
              id="country"
              name="country"
              ref={register({ required: t<string>('error.required') })}
              isRequired
              isInvalid={!!errors.country}
            >
              <option value="" disabled>
                {t('placeholder.country')}
              </option>
              {COUNTRIES.map(({ label, value }) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField {...sharedProps} error={errors.city} fieldName="city" type="text" isRequired />

          <FormField
            {...sharedProps}
            error={errors.zipCode}
            fieldName="zipCode"
            type="text"
            isRequired
          />
        </Stack>

        <Stack preset="default">
          <FormField {...sharedProps} error={errors.county} fieldName="county" type="text" />

          {showState ? (
            <FormField {...sharedProps} error={errors.state} fieldName="state" isRequired>
              <Select
                id="state"
                name="state"
                ref={register({ required: t<string>('error.required') })}
                isInvalid={!!errors.state}
              >
                <option value="">{t('placeholder.state')}</option>
                {STATES[watchCountry as keyof typeof STATES].map(
                  (state: (typeof STATES)[keyof typeof STATES][number]) => (
                    <option value={state.value} key={state.value}>
                      {state.label}
                    </option>
                  ),
                )}
              </Select>
            </FormField>
          ) : (
            // a naive quick spacer, but it works soooo ¯\_(ツ)_/¯
            <div style={{ width: '100%' }} />
          )}
        </Stack>

        <Text as="p" textType="subtitle1">
          {t('subtitle.organization-information')}
        </Text>

        <FormField
          {...sharedProps}
          error={errors.companyName}
          fieldName="companyName"
          type="text"
          isRequired
        />

        <FormField
          {...sharedProps}
          error={errors.taxNumber}
          fieldName="taxNumber"
          type="text"
          isRequired={showState}
        />

        <Text as="p" textType="subtitle1">
          {t('subtitle.billing-notifications')}
        </Text>

        <Stack preset="default">
          <FormField
            {...sharedProps}
            error={errors.firstName}
            fieldName="firstName"
            type="text"
            isRequired
          />
          <FormField
            {...sharedProps}
            error={errors.lastName}
            fieldName="lastName"
            type="text"
            isRequired
          />
        </Stack>

        <FormField
          {...sharedProps}
          error={errors.emailAddress}
          fieldName="emailAddress"
          type="email"
          isRequired
        />
      </Stack>
    </form>
  );
}
